import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';

const ASPECT_RATIO = { 'id-1': 1.586, passport: 0.71 }; // ID-1 landscape; passport leaf held upright (portrait)
const HD = { width: { ideal: 3840 }, height: { ideal: 2160 } };

// Guided overlay camera (distinct from the kiosk DocumentCamera): scrim + framed cutout + per-side header.
// Emits JPEG so the upload stays under the agent's 1MB cap without a destructive resize.
const GuidedDocumentCamera = ({ side, aspect = 'id-1', header, text, cameraIncludes, cameraRotationAngle, onCapture, onClose, onPickFile }) => {
	const videoRef = useRef();
	const canvasRef = useRef();
	const streamRef = useRef();
	const mountedRef = useRef(true);
	const [loading, setLoading] = useState(true);
	const [denied, setDenied] = useState(false);
	const [cameras, setCameras] = useState([]);
	const [activeIndex, setActiveIndex] = useState(0);
	const isSmall = useMediaQuery('(max-width:600px)'); // ~MUI sm; string form needs no ThemeProvider

	const attach = useCallback(async (node) => {
		try {
			const pre = await navigator.mediaDevices.enumerateDevices();
			const match = cameraIncludes ? pre.find((d) => d.kind === 'videoinput' && d.label.includes(cameraIncludes)) : undefined;
			// Stop any prior stream and bail if we unmounted mid-getUserMedia, else the camera track leaks.
			streamRef.current?.getTracks().forEach((t) => t.stop());
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: false,
				video: match ? { deviceId: match.deviceId, ...HD } : { facingMode: 'environment', ...HD },
			});
			if (!mountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
			streamRef.current = stream;
			node.srcObject = stream;
			// Device labels populate only AFTER permission is granted — enumerate again for the switch list.
			const inputs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
			if (!mountedRef.current) return;
			setCameras(inputs);
			const activeId = stream.getVideoTracks?.()[0]?.getSettings?.().deviceId;
			const idx = inputs.findIndex((d) => d.deviceId === activeId);
			setActiveIndex(idx === -1 ? 0 : idx);
			setDenied(false);
		} catch {
			if (mountedRef.current) setDenied(true);
		}
		if (mountedRef.current) setLoading(false);
	}, [cameraIncludes]);

	const handleVideoRef = useCallback((node) => { videoRef.current = node; if (node) attach(node); }, [attach]);

	// Re-arm mountedRef on every mount so a remount (e.g. React StrictMode's dev mount→cleanup→mount) doesn't
	// leave it stuck false — otherwise the resolved getUserMedia stream gets stopped and the spinner never clears.
	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; streamRef.current?.getTracks().forEach((t) => t.stop()); };
	}, []);

	const switchCamera = useCallback(async () => {
		if (cameras.length < 2) return;
		const next = (activeIndex + 1) % cameras.length; // cycle — handles 2 or many cameras
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { deviceId: { exact: cameras[next].deviceId }, ...HD } });
			streamRef.current?.getTracks().forEach((t) => t.stop());
			streamRef.current = stream;
			if (videoRef.current) videoRef.current.srcObject = stream;
			setActiveIndex(next);
		} catch { /* keep the current camera if the switch fails */ }
	}, [cameras, activeIndex]);

	const ratio = ASPECT_RATIO[aspect] || ASPECT_RATIO['id-1'];

	const capture = useCallback(() => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (!video || !canvas) return;
		const vw = video.videoWidth;
		const vh = video.videoHeight;
		if (!vw || !vh) return;
		// Crop to the on-screen cutout (+ small margin) so the saved image matches what the user framed.
		// Feed is object-fit:cover, so map the centred cutout to video pixels via the cover scale.
		const cw = video.clientWidth;
		const ch = video.clientHeight;
		const MARGIN = 1.08;
		let cropW;
		if (cw && ch) {
			const scale = Math.max(cw / vw, ch / vh); // cover: container px per video px
			cropW = (Math.min(0.86 * cw, 380) * MARGIN) / scale;
		} else {
			cropW = vw * 0.9;
		}
		let cropH = cropW / ratio;
		if (cropW > vw) { cropW = vw; cropH = cropW / ratio; }
		if (cropH > vh) { cropH = vh; cropW = cropH * ratio; }
		const sx = (vw - cropW) / 2;
		const sy = (vh - cropH) / 2;
		canvas.width = Math.round(cropW);
		canvas.height = Math.round(cropH);
		canvas.getContext('2d')?.drawImage(video, sx, sy, cropW, cropH, 0, 0, canvas.width, canvas.height);
		canvas.toBlob((blob) => { if (blob) onCapture(blob); }, 'image/jpeg', 0.9);
	}, [onCapture, ratio]);

	return (
		<Dialog
			open
			fullScreen={isSmall}
			PaperProps={{ sx: { bgcolor: '#11131c', m: 0, width: isSmall ? '100%' : 'min(92vw, 460px)', height: isSmall ? '100%' : 'min(88vh, 680px)' } }}
		>
			<Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
				{denied ? (
					<Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, color: 'common.white', p: 3, textAlign: 'center' }}>
						<Typography sx={{ fontWeight: 600 }}>{text.cameraDenied}</Typography>
						{text.cameraDeniedHint && (
							<Typography variant="body2" sx={{ color: 'rgba(255,255,255,.75)', maxWidth: 360 }}>{text.cameraDeniedHint}</Typography>
						)}
						<Button variant="contained" onClick={onPickFile}>{text.cameraDeniedAction}</Button>
						<Button color="inherit" sx={{ color: 'common.white' }} onClick={onClose}>{text.camCancel}</Button>
					</Box>
				) : (
					<>
						<video ref={handleVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: cameraRotationAngle ? `rotate(${cameraRotationAngle}deg)` : 'none' }} />
						{loading && <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>}

						<Typography sx={{ position: 'absolute', top: 24, left: 0, right: 0, textAlign: 'center', color: 'common.white', fontWeight: 600, px: 3, textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>{header}</Typography>

						{/* Cutout: a transparent window punched through the dark scrim via a huge box-shadow; capped so it stays card-sized on big screens. */}
						<Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(86%, 380px)', aspectRatio: String(ratio), borderRadius: 2, border: '2px solid rgba(255,255,255,.9)', boxShadow: '0 0 0 2000px rgba(10,12,20,.7)' }}>
							{['tl', 'tr', 'bl', 'br'].map((corner) => (
								<Box key={corner} sx={{
									position: 'absolute', width: 22, height: 22, borderColor: 'common.white', borderStyle: 'solid', borderWidth: 0,
									...(corner[0] === 't' ? { top: -2, borderTopWidth: 3 } : { bottom: -2, borderBottomWidth: 3 }),
									...(corner[1] === 'l' ? { left: -2, borderLeftWidth: 3 } : { right: -2, borderRightWidth: 3 }),
								}} />
							))}
						</Box>

						<Box sx={{ position: 'absolute', bottom: 24, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, zIndex: 1 }}>
							<Button color="inherit" sx={{ color: 'common.white' }} onClick={onClose}>{text.camCancel}</Button>
							<Button variant="contained" disabled={loading} onClick={capture} data-testid="cam-shutter">{text.camCapture}</Button>
							{cameras.length > 1 && (
								<IconButton data-testid="cam-switch" onClick={switchCamera} aria-label="Switch camera" sx={{ color: 'common.white' }}>
									<CameraswitchIcon />
								</IconButton>
							)}
						</Box>
					</>
				)}
				<canvas ref={canvasRef} style={{ display: 'none' }} />
			</Box>
		</Dialog>
	);
};

export default GuidedDocumentCamera;
